import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonLoading,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonViewWillEnter,
} from "@ionic/react";
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Stack,
  Button,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  DateRange,
  Search,
  Refresh,
  Download,
  FilterList,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  AccountBalance,
  CalendarMonth,
  Info,
  ViewList,
  ViewDay,
  Print as PrintIcon,
  Warning,
} from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { requestID, baseUrl, config } from "../../common/utils";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useBusinessContext } from "../../context/business-context";
import ReactToPrint from "react-to-print";
import { useReactToPrint } from 'react-to-print';
import { MenuButton } from "../../components/Menu";

const LedgerReport_sales_only = () => {
  const theme = useTheme();
  const componentRef = useRef();
  const printContentRef = useRef();
  
  const schema = yup
    .object({
      ledger: yup.string().required("Please select a ledger"),
    })
    .required();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ledger: "",
    },
  });

  const { name } = useParams<{ name: string }>();
  const { selectedBusiness } = useBusinessContext();

  const [status, setStatus] = useState({ loading: false, error: false });
  const [errorLogin, setErrorLogin] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [posting, setPosting] = useState([]);
  const [allLedger, setAllLedger] = useState([]);
  const [summary, setSummary] = useState({
    totalExpenses: 0,
    totalProfit: 0,
    totalLoss: 0,
    netBalance: 0,
    transactionCount: 0,
  });

  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [runningBalance, setRunningBalance] = useState(0);
  const [viewMode, setViewMode] = useState('detailed');
  const [dailySummary, setDailySummary] = useState([]);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  const getLedgers = () => {
    const formData = new FormData();
    formData.append("requestID", requestID());
    
    setStatus({ loading: true, error: false });
    axios
      .post(baseUrl() + "ledgercreates/getAllLedger_caas_branch_sales_only", formData, config)
      .then((response) => {
        setAllLedger(response.data.data || []);
        setStatus({ loading: false, error: false });
      })
      .catch((error) => {
        console.error("Error fetching ledgers:", error);
        setStatus({ loading: false, error: false });
      });
  };

  useIonViewWillEnter(() => {
    getLedgers();
  });

  useEffect(() => {
    getLedgers();
  }, []);

  // Helper function to extract loss amount from itemCode
  const extractLossAmount = (itemCode) => {
    if (!itemCode) return 0;
    
    // Try to parse the itemCode as a number
    const parsed = parseFloat(itemCode);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Function to group transactions by day
  const groupTransactionsByDay = (transactions) => {
    const grouped = {};
    
    transactions.forEach((transaction) => {
      const date = dayjs(transaction.dateCreated).format('YYYY-MM-DD');
      const formattedDate = dayjs(transaction.dateCreated).format('DD MMM YYYY');
      const dayOfWeek = dayjs(transaction.dateCreated).format('dddd');
      
      if (!grouped[date]) {
        grouped[date] = {
          date: date,
          formattedDate: formattedDate,
          dayOfWeek: dayOfWeek,
          totalExpenses: 0,
          totalProfit: 0,
          totalLoss: 0,
          transactionCount: 0,
          transactions: [],
          netBalance: 0,
          runningBalance: 0
        };
      }
      
      const expense = parseFloat(transaction.dr) || 0;
      const profit = parseFloat(transaction.cr) || 0;
      // Extract loss from itemCode if progID is LOSS
      const loss = transaction.progID === 'LOSS' ? extractLossAmount(transaction.itemCode) : 0;
      
      grouped[date].totalExpenses += expense;
      grouped[date].totalProfit += profit;
      grouped[date].totalLoss += loss;
      grouped[date].transactionCount += 1;
      grouped[date].transactions.push(transaction);
      grouped[date].netBalance = grouped[date].totalProfit - grouped[date].totalExpenses - grouped[date].totalLoss;
    });
    
    // Convert to array and sort by date
    const result = Object.values(grouped).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    // Calculate running balance for daily summary
    let cumulativeBalance = 0;
    result.forEach((day) => {
      cumulativeBalance += day.netBalance;
      day.runningBalance = cumulativeBalance;
    });
    
    return result;
  };

  const getPostingReport = async (data) => {
    if (!data.ledger) {
      setErrorLogin("Please select a ledger first");
      setShowToast(true);
      return;
    }
    
    setStatus({ loading: true, error: false });
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("startDate", startDate.format('YYYY-MM-DD'));
      formData.append("endDate", endDate.format('YYYY-MM-DD'));
      formData.append("sn", data.ledger);
      
      const response = await axios.post(
        baseUrl() + "ledgers/getPostingReport_sales_only_xchange",
        formData,
        config
      );
      
      const dataResponse = response.data.data || [];
      
      // Sort by date ascending for proper running balance calculation
      const sortedData = [...dataResponse].sort((a, b) => 
        new Date(a.dateCreated) - new Date(b.dateCreated)
      );
      
      setPosting(sortedData);
      
      // Calculate daily summary
      const dailyGrouped = groupTransactionsByDay(sortedData);
      setDailySummary(dailyGrouped);
      
      // Calculate summary for four columns
      const totalExpenses = sortedData.reduce((sum, item) => sum + (parseFloat(item.dr) || 0), 0);
      const totalProfit = sortedData.reduce((sum, item) => sum + (parseFloat(item.cr) || 0), 0);
      const totalLoss = sortedData.reduce((sum, item) => {
        // Check if transaction is a loss (progID == 'LOSS')
        // If yes, extract loss amount from itemCode
        if (item.progID === 'LOSS') {
          return sum + extractLossAmount(item.itemCode);
        }
        return sum;
      }, 0);
      const netBalance = totalProfit - totalExpenses - totalLoss; // Profit - Expenses - Loss
      
      // Calculate final running balance
      let calculatedRunningBalance = 0;
      sortedData.forEach((item) => {
        const profit = parseFloat(item.cr) || 0;
        const expense = parseFloat(item.dr) || 0;
        const loss = item.progID === 'LOSS' ? extractLossAmount(item.itemCode) : 0;
        calculatedRunningBalance += (profit - expense - loss);
      });
      
      setRunningBalance(calculatedRunningBalance);
      
      setSummary({
        totalExpenses,
        totalProfit,
        totalLoss,
        netBalance,
        transactionCount: sortedData.length,
      });
      
      setStatus({ loading: false, error: false });
    } catch (error) {
      console.error("Error fetching posting report:", error);
      setStatus({ loading: false, error: true });
      setErrorLogin("Failed to fetch report data");
      setShowToast(true);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const handleExport = () => {
    console.log("Exporting data...");
  };

  const handleRefresh = () => {
    const selectedLedger = watch("ledger");
    if (selectedLedger) {
      getPostingReport({ ledger: selectedLedger });
    }
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handlePrintOpen = () => {
    setPrintDialogOpen(true);
  };

  const handlePrintClose = () => {
    setPrintDialogOpen(false);
  };

  // Function to calculate running balance per row for detailed view
  const calculateRunningBalance = (transactions, currentIndex) => {
    let balance = 0;
    for (let i = 0; i <= currentIndex; i++) {
      const profit = parseFloat(transactions[i]?.cr) || 0;
      const expense = parseFloat(transactions[i]?.dr) || 0;
      const loss = transactions[i]?.progID === 'LOSS' ? extractLossAmount(transactions[i]?.itemCode) : 0;
      balance += (profit - expense - loss);
    }
    return balance;
  };

  // Calculate totals for current view
  const getViewTotals = () => {
    if (viewMode === 'detailed') {
      return summary;
    } else {
      // Calculate from daily summary
      const totalExpenses = dailySummary.reduce((sum, day) => sum + day.totalExpenses, 0);
      const totalProfit = dailySummary.reduce((sum, day) => sum + day.totalProfit, 0);
      const totalLoss = dailySummary.reduce((sum, day) => sum + day.totalLoss, 0);
      const netBalance = dailySummary.reduce((sum, day) => sum + day.netBalance, 0);
      const transactionCount = dailySummary.reduce((sum, day) => sum + day.transactionCount, 0);
      
      return {
        totalExpenses,
        totalProfit,
        totalLoss,
        netBalance,
        transactionCount
      };
    }
  };

  const viewTotals = getViewTotals();
  const selectedLedger = watch("ledger");
  const selectedLedgerName = allLedger.find(l => l.sn === selectedLedger)?.name || "";

  // Print content component
  const PrintContent = React.forwardRef(({ posting, viewMode, dailySummary, viewTotals, selectedLedgerName, startDate, endDate }, ref) => {
    const calculateRunningBalanceForPrint = (transactions, currentIndex) => {
      let balance = 0;
      for (let i = 0; i <= currentIndex; i++) {
        const profit = parseFloat(transactions[i]?.cr) || 0;
        const expense = parseFloat(transactions[i]?.dr) || 0;
        const loss = transactions[i]?.progID === 'LOSS' ? extractLossAmount(transactions[i]?.itemCode) : 0;
        balance += (profit - expense - loss);
      }
      return balance;
    };

    return (
      <div ref={ref} style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: '2px solid #333', paddingBottom: '20px' }}>
          <h1 style={{ margin: 0, fontSize: '28px' }}>Ledger Report</h1>
          <h2 style={{ margin: '10px 0 0 0', fontSize: '20px', color: '#666' }}>{selectedLedgerName}</h2>
          <p style={{ margin: '5px 0', fontSize: '14px' }}>
            Period: {formatDate(startDate)} to {formatDate(endDate)}
          </p>
          <p style={{ margin: '5px 0', fontSize: '14px' }}>
            View Mode: {viewMode === 'detailed' ? 'Detailed Transactions' : 'Daily Summary'}
          </p>
          <p style={{ margin: '5px 0', fontSize: '14px' }}>
            Printed on: {dayjs().format('DD MMM YYYY HH:mm')}
          </p>
        </div>

        {/* Summary Section - Updated for 4 columns */}
        <div style={{ marginBottom: '30px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
          <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '5px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>Total Expenses (Dr)</h3>
            <p style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#d32f2f' }}>
              {formatCurrency(viewTotals.totalExpenses)}
            </p>
          </div>
          <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '5px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>Total Profit (Cr)</h3>
            <p style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#2e7d32' }}>
              {formatCurrency(viewTotals.totalProfit)}
            </p>
          </div>
          <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '5px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>Total Loss</h3>
            <p style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#f57c00' }}>
              {formatCurrency(viewTotals.totalLoss)}
            </p>
          </div>
          <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '5px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>Net Balance</h3>
            <p style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: 'bold', 
              color: viewTotals.netBalance >= 0 ? '#2e7d32' : '#d32f2f' 
            }}>
              {formatCurrency(viewTotals.netBalance)}
            </p>
            <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
              (Profit - Expenses - Loss)
            </p>
          </div>
        </div>

        {/* Table Section - Updated for 4 columns */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px' }}>
            {viewMode === 'detailed' ? 'Transaction Details' : 'Daily Summary'}
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                {viewMode === 'detailed' ? (
                  <>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>#</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Expenses (Dr)</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Profit (Cr)</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Loss (ItemCode)</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Balance (Cr-Dr-Loss)</th>
                  </>
                ) : (
                  <>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>#</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Day</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Transactions</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Total Expenses</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Total Profit</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Total Loss</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Daily Net</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Cumulative Balance</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {viewMode === 'detailed' ? (
                posting.map((transaction, index) => {
                  const profit = parseFloat(transaction.cr) || 0;
                  const expense = parseFloat(transaction.dr) || 0;
                  const loss = transaction.progID === 'LOSS' ? extractLossAmount(transaction.itemCode) : 0;
                  const transactionBalance = profit - expense - loss;
                  const cumulativeBalance = calculateRunningBalanceForPrint(posting, index);
                  
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{index + 1}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                        {formatDate(transaction.dateCreated)}
                        <br />
                        <small>{dayjs(transaction.dateCreated).format('HH:mm')}</small>
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                        {transaction.desp}
                        {transaction.progID === 'LOSS' && (
                          <div>
                            <br />
                            <small style={{ color: '#f57c00' }}><strong>LOSS TRANSACTION</strong></small>
                          </div>
                        )}
                        {transaction.reference && (
                          <div>
                            <br />
                            <small>Ref: {transaction.reference}</small>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: expense > 0 ? '#d32f2f' : 'inherit' }}>
                        {expense > 0 && formatCurrency(expense)}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: profit > 0 ? '#2e7d32' : 'inherit' }}>
                        {profit > 0 && formatCurrency(profit)}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: loss > 0 ? '#f57c00' : 'inherit' }}>
                        {loss > 0 && (
                          <>
                            {formatCurrency(loss)}
                            <br />
                            <small>From: {transaction.itemCode}</small>
                          </>
                        )}
                      </td>
                      <td style={{ 
                        padding: '10px', 
                        border: '1px solid #ddd', 
                        textAlign: 'right',
                        fontWeight: 'bold',
                        color: cumulativeBalance >= 0 ? '#2e7d32' : '#d32f2f' 
                      }}>
                        {formatCurrency(cumulativeBalance)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                dailySummary.map((day, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{index + 1}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                      {day.formattedDate}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', color: '#666' }}>
                      {day.dayOfWeek}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                      {day.transactionCount} {day.transactionCount === 1 ? 'transaction' : 'transactions'}
                    </td>
                    <td style={{ 
                      padding: '10px', 
                      border: '1px solid #ddd', 
                      textAlign: 'right',
                      color: day.totalExpenses > 0 ? '#d32f2f' : 'inherit'
                    }}>
                      {day.totalExpenses > 0 && formatCurrency(day.totalExpenses)}
                    </td>
                    <td style={{ 
                      padding: '10px', 
                      border: '1px solid #ddd', 
                      textAlign: 'right',
                      color: day.totalProfit > 0 ? '#2e7d32' : 'inherit'
                    }}>
                      {day.totalProfit > 0 && formatCurrency(day.totalProfit)}
                    </td>
                    <td style={{ 
                      padding: '10px', 
                      border: '1px solid #ddd', 
                      textAlign: 'right',
                      color: day.totalLoss > 0 ? '#f57c00' : 'inherit'
                    }}>
                      {day.totalLoss > 0 && formatCurrency(day.totalLoss)}
                    </td>
                    <td style={{ 
                      padding: '10px', 
                      border: '1px solid #ddd', 
                      textAlign: 'right',
                      fontWeight: 'bold',
                      color: day.netBalance >= 0 ? '#2e7d32' : '#d32f2f'
                    }}>
                      {formatCurrency(day.netBalance)}
                    </td>
                    <td style={{ 
                      padding: '10px', 
                      border: '1px solid #ddd', 
                      textAlign: 'right',
                      fontWeight: 'bold',
                      color: day.runningBalance >= 0 ? '#2e7d32' : '#d32f2f'
                    }}>
                      {formatCurrency(day.runningBalance)}
                    </td>
                  </tr>
                ))
              )}
              
              {/* Totals Row */}
              <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                {viewMode === 'detailed' ? (
                  <>
                    <td colSpan="3" style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>
                      Totals:
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#d32f2f' }}>
                      {formatCurrency(viewTotals.totalExpenses)}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#2e7d32' }}>
                      {formatCurrency(viewTotals.totalProfit)}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#f57c00' }}>
                      {formatCurrency(viewTotals.totalLoss)}
                    </td>
                    <td style={{ 
                      padding: '10px', 
                      border: '1px solid #ddd', 
                      textAlign: 'right',
                      color: viewTotals.netBalance >= 0 ? '#2e7d32' : '#d32f2f'
                    }}>
                      {formatCurrency(viewTotals.netBalance)}
                    </td>
                  </>
                ) : (
                  <>
                    <td colSpan="4" style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>
                      Period Totals:
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#d32f2f' }}>
                      {formatCurrency(viewTotals.totalExpenses)}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#2e7d32' }}>
                      {formatCurrency(viewTotals.totalProfit)}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#f57c00' }}>
                      {formatCurrency(viewTotals.totalLoss)}
                    </td>
                    <td style={{ 
                      padding: '10px', 
                      border: '1px solid #ddd', 
                      textAlign: 'right',
                      color: viewTotals.netBalance >= 0 ? '#2e7d32' : '#d32f2f'
                    }}>
                      {formatCurrency(viewTotals.netBalance)}
                    </td>
                    <td style={{ 
                      padding: '10px', 
                      border: '1px solid #ddd', 
                      textAlign: 'right',
                      color: viewTotals.netBalance >= 0 ? '#2e7d32' : '#d32f2f'
                    }}>
                      {formatCurrency(viewTotals.netBalance)}
                    </td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #ddd', textAlign: 'center', color: '#666' }}>
          <p style={{ margin: 0, fontSize: '12px' }}>
            Generated by Sales Ledger System • {selectedLedgerName}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px' }}>
            Page 1 of 1 • Printed on {dayjs().format('DD/MM/YYYY HH:mm:ss')}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px' }}>
            Formula: Net Balance = Profit (Cr) - Expenses (Dr) - Loss 
          </p>
        </div>
      </div>
    );
  });

  return (
    <IonPage className="pushDown">
                                      <MenuButton /> {/* This adds the hamburger icon */}
      
      <IonContent fullscreen>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" fontWeight="600" gutterBottom>
              Ledger Report
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View detailed transaction history with Expenses, Profit, Loss, and Balance columns
            </Typography>
          </Box>

          {/* Filter Card */}
          <Card sx={{ mb: 4, borderRadius: 2, boxShadow: theme.shadows[2] }}>
            <CardHeader
              title="Report Filters"
              avatar={<FilterList />}
              action={
                <Stack direction="row" spacing={1}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    <ToggleButton value="detailed" disabled={posting.length === 0}>
                      <Tooltip title="Detailed View">
                        <ViewList />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="summarized" disabled={posting.length === 0}>
                      <Tooltip title="Daily Summary">
                        <ViewDay />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    startIcon={<PrintIcon />}
                    onClick={handlePrintOpen}
                    disabled={posting.length === 0}
                    variant="outlined"
                    size="small"
                    color="info"
                  >
                    Print
                  </Button>
                  <Button
                    startIcon={<Download />}
                    onClick={handleExport}
                    disabled={posting.length === 0}
                    variant="outlined"
                    size="small"
                  >
                    Export
                  </Button>
                </Stack>
              }
            />
            <Divider />
            <CardContent>
              <form onSubmit={handleSubmit(getPostingReport)}>
                <Grid container spacing={3}>
                  {/* Ledger Selection */}
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="medium">
                      <InputLabel id="ledger-select-label">Select Ledger</InputLabel>
                      <Controller
                        name="ledger"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="ledger-select-label"
                            label="Select Ledger"
                            error={!!errors.ledger}
                            disabled={status.loading}
                            startAdornment={<AccountBalance sx={{ mr: 1 }} />}
                          >
                            <MenuItem value="">
                              <em>Select a ledger...</em>
                            </MenuItem>
                            {allLedger.map((ledger, index) => (
                              <MenuItem key={index} value={ledger.sn}>
                                {ledger.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.ledger && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                          {errors.ledger.message}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Date Range */}
                  <Grid item xs={12} md={5}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            maxDate={endDate}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "medium",
                                InputProps: {
                                  startAdornment: <CalendarMonth sx={{ mr: 1, color: 'action.active' }} />,
                                },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            minDate={startDate}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "medium",
                                InputProps: {
                                  startAdornment: <CalendarMonth sx={{ mr: 1, color: 'action.active' }} />,
                                },
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </LocalizationProvider>
                  </Grid>

                  {/* Generate Button */}
                  <Grid item xs={12} md={3}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={status.loading}
                      startIcon={<Search />}
                      sx={{
                        height: '56px',
                        borderRadius: 1,
                        fontWeight: 600,
                      }}
                    >
                      Generate Report
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>

          {/* Summary Cards - Updated for 4 columns */}
          {posting.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.08) }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          bgcolor: alpha(theme.palette.error.main, 0.2),
                          p: 1.5,
                          borderRadius: 2,
                        }}
                      >
                        <TrendingDown sx={{ color: theme.palette.error.main }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Expenses (Dr)
                        </Typography>
                        <Typography variant="h5" fontWeight="600" color="error.main">
                          {formatCurrency(viewTotals.totalExpenses)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          All debit transactions
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.08) }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.2),
                          p: 1.5,
                          borderRadius: 2,
                        }}
                      >
                        <TrendingUp sx={{ color: theme.palette.success.main }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Profit (Cr)
                        </Typography>
                        <Typography variant="h5" fontWeight="600" color="success.main">
                          {formatCurrency(viewTotals.totalProfit)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          All credit transactions
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.08) }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          bgcolor: alpha(theme.palette.warning.main, 0.2),
                          p: 1.5,
                          borderRadius: 2,
                        }}
                      >
                        <Warning sx={{ color: theme.palette.warning.main }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Loss
                        </Typography>
                        <Typography variant="h5" fontWeight="600" color="warning.main">
                          {formatCurrency(viewTotals.totalLoss)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          All Loss
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.08) }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.2),
                          p: 1.5,
                          borderRadius: 2,
                        }}
                      >
                        <AttachMoney sx={{ color: theme.palette.info.main }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Net Balance
                        </Typography>
                        <Typography 
                          variant="h5" 
                          fontWeight="600"
                          color={viewTotals.netBalance >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(viewTotals.netBalance)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Profit - Expenses - Loss
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Transactions Table - Updated for 4 columns */}
          <Card sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
            <CardHeader
              title={
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {viewMode === 'detailed' ? 'Transaction History' : 'Daily Summary'}
                  </Typography>
                  <Chip 
                    label={`${viewMode === 'detailed' ? 'Detailed' : 'Summarized'} View`} 
                    color="primary"
                    size="small"
                    icon={viewMode === 'detailed' ? <ViewList /> : <ViewDay />}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Balance = Profit - Expenses - Loss
                  </Typography>
                </Stack>
              }
              subheader={
                viewMode === 'detailed' 
                  ? `Showing ${posting.length} individual transactions`
                  : `Showing ${dailySummary.length} daily summaries (${summary.transactionCount} total transactions)`
              }
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton 
                    color="info" 
                    onClick={handlePrintOpen}
                    disabled={posting.length === 0}
                    size="small"
                  >
                    <Tooltip title="Print Report">
                      <PrintIcon />
                    </Tooltip>
                  </IconButton>
                  <Typography variant="caption" color="text.secondary">
                    View:
                  </Typography>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                  >
                    <ToggleButton value="detailed">
                      <Tooltip title="Detailed View">
                        <ViewList />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="summarized">
                      <Tooltip title="Daily Summary">
                        <ViewDay />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {posting.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                        {viewMode === 'detailed' ? (
                          <>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Description</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Expenses (Dr)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Profit (Cr)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Loss (ItemCode)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Balance (Cr-Dr-Loss)</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Day</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Transactions</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Total Expenses</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Total Profit</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Total Loss</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Daily Net</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Cumulative Balance</TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewMode === 'detailed' ? (
                        // Detailed View - 4 columns
                        posting.map((transaction, index) => {
                          const profit = parseFloat(transaction.cr) || 0;
                          const expense = parseFloat(transaction.dr) || 0;
                          const loss = transaction.progID === 'LOSS' ? extractLossAmount(transaction.itemCode) : 0;
                          const transactionBalance = profit - expense - loss;
                          const cumulativeBalance = calculateRunningBalance(posting, index);
                          
                          return (
                            <TableRow 
                              key={index}
                              sx={{ 
                                '&:hover': { 
                                  bgcolor: theme.palette.action.hover 
                                },
                                borderBottom: index < posting.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                              }}
                            >
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {index + 1}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2">
                                  {formatDate(transaction.dateCreated)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {dayjs(transaction.dateCreated).format('HH:mm')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2">
                                  {transaction.desp}
                                </Typography>
                                {transaction.progID === 'LOSS' && (
                                  <Typography variant="caption" color="warning.main" fontWeight="bold">
                                    LOSS TRANSACTION
                                  </Typography>
                                )}
                                {transaction.reference && (
                                  <Typography variant="caption" color="text.secondary">
                                    Ref: {transaction.reference}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {expense > 0 && (
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="500"
                                    color="error.main"
                                  >
                                    {formatCurrency(expense)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {profit > 0 && (
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="500"
                                    color="success.main"
                                  >
                                    {formatCurrency(profit)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {loss > 0 && (
                                  <>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="500"
                                      color="warning.main"
                                    >
                                      {formatCurrency(loss)}
                                    </Typography>
                                    <Typography 
                                      variant="caption" 
                                      color="warning.main"
                                      sx={{ display: 'block' }}
                                    >
                                      ItemCode: {transaction.itemCode}
                                    </Typography>
                                  </>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={cumulativeBalance >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(cumulativeBalance)}
                                </Typography>
                                {transactionBalance !== 0 && (
                                  <Typography 
                                    variant="caption" 
                                    color={transactionBalance > 0 ? 'success.main' : 'error.main'}
                                    sx={{ display: 'block' }}
                                  >
                                    {transactionBalance > 0 ? '+' : ''}{formatCurrency(transactionBalance)}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        // Summarized View (Daily) - 5 columns
                        dailySummary.map((day, index) => (
                          <TableRow 
                            key={index}
                            sx={{ 
                              '&:hover': { 
                                bgcolor: theme.palette.action.hover 
                              },
                              borderBottom: index < dailySummary.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                            }}
                          >
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                {index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" fontWeight="500">
                                {day.formattedDate}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                {day.dayOfWeek}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Chip 
                                label={`${day.transactionCount} trans`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {day.transactionCount === 1 ? '1 transaction' : `${day.transactionCount} transactions`}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              {day.totalExpenses > 0 && (
                                <Typography 
                                  variant="body2" 
                                  fontWeight="500"
                                  color="error.main"
                                >
                                  {formatCurrency(day.totalExpenses)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              {day.totalProfit > 0 && (
                                <Typography 
                                  variant="body2" 
                                  fontWeight="500"
                                  color="success.main"
                                >
                                  {formatCurrency(day.totalProfit)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              {day.totalLoss > 0 && (
                                <Typography 
                                  variant="body2" 
                                  fontWeight="500"
                                  color="warning.main"
                                >
                                  {formatCurrency(day.totalLoss)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Typography 
                                variant="body2" 
                                fontWeight="600"
                                color={day.netBalance >= 0 ? 'success.main' : 'error.main'}
                              >
                                {formatCurrency(day.netBalance)}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color={day.netBalance >= 0 ? 'success.main' : 'error.main'}
                                sx={{ display: 'block' }}
                              >
                                {day.netBalance > 0 ? '+' : ''}{formatCurrency(day.netBalance)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Typography 
                                variant="body2" 
                                fontWeight="600"
                                color={day.runningBalance >= 0 ? 'success.main' : 'error.main'}
                              >
                                {formatCurrency(day.runningBalance)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      
                      {/* Footer Row with Totals */}
                      {posting.length > 0 && (
                        <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                          {viewMode === 'detailed' ? (
                            <>
                              <TableCell colSpan={3} align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600">
                                  Totals:
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {viewMode === 'summarized' ? `${dailySummary.length} days` : `${summary.transactionCount} transactions`}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600" color="error.main">
                                  {formatCurrency(viewTotals.totalExpenses)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600" color="success.main">
                                  {formatCurrency(viewTotals.totalProfit)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600" color="warning.main">
                                  {formatCurrency(viewTotals.totalLoss)}
                                </Typography>
                                {/* <Typography variant="caption" color="text.secondary">
                                  From ItemCode where progID = 'LOSS'
                                </Typography> */}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography 
                                  variant="body1" 
                                  fontWeight="600"
                                  color={viewTotals.netBalance >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(viewTotals.netBalance)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Profit - Expenses - Loss
                                </Typography>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell colSpan={4} align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600">
                                  Period Totals:
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {dailySummary.length} days
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600" color="error.main">
                                  {formatCurrency(viewTotals.totalExpenses)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600" color="success.main">
                                  {formatCurrency(viewTotals.totalProfit)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600" color="warning.main">
                                  {formatCurrency(viewTotals.totalLoss)}
                                </Typography>
                                {/* <Typography variant="caption" color="text.secondary">
                                  From ItemCode where progID = 'LOSS'
                                </Typography> */}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography 
                                  variant="body1" 
                                  fontWeight="600"
                                  color={viewTotals.netBalance >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(viewTotals.netBalance)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Daily net total
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography 
                                  variant="body1" 
                                  fontWeight="600"
                                  color={viewTotals.netBalance >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(viewTotals.netBalance)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Final balance
                                </Typography>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <DateRange sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Transactions Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {watch("ledger") 
                      ? "No transactions found for the selected ledger and date range" 
                      : "Select a ledger and date range to view transactions"}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Info Alert */}
          {posting.length > 0 && (
            <Alert 
              severity="info" 
              sx={{ mt: 3, borderRadius: 2 }}
              icon={<Info />}
            >
              <Typography variant="body2">
                {viewMode === 'detailed' 
                  ? `Showing ${posting.length} individual transactions from ${formatDate(startDate)} to ${formatDate(endDate)}.`
                  : `Showing ${dailySummary.length} daily summaries aggregating ${summary.transactionCount} total transactions from ${formatDate(startDate)} to ${formatDate(endDate)}.`}
                {" "}Balance calculation: Profit (Cr) - Expenses (Dr) - Loss
                <br />
                <strong>View Mode:</strong> {viewMode === 'detailed' ? 'Detailed transaction list' : 'Daily aggregated summary'}
                <br />
                {/* <strong>Loss Column:</strong> Shows amount from ItemCode where progID = 'LOSS' */}
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Print Dialog */}
        <Dialog open={printDialogOpen} onClose={handlePrintClose} maxWidth="lg" fullWidth>
          <DialogTitle>Print Report</DialogTitle>
          <DialogContent>
            <div ref={printContentRef}>
              <PrintContent 
                posting={posting}
                viewMode={viewMode}
                dailySummary={dailySummary}
                viewTotals={viewTotals}
                selectedLedgerName={selectedLedgerName}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePrintClose}>Cancel</Button>
            <Button 
              variant="contained" 
              startIcon={<PrintIcon />}
              color="primary"
              onClick={() => {
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                  const printContent = printContentRef.current;
                  if (printContent) {
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <title>Ledger Report - ${selectedLedgerName}</title>
                          <style>
                            @page { size: A4 portrait; margin: 20mm; }
                            body { font-family: Arial, sans-serif; }
                            table { width: 100%; border-collapse: collapse; font-size: 12px; }
                            th, td { padding: 10px; border: 1px solid #ddd; }
                            th { background-color: #f5f5f5; font-weight: 600; }
                            .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
                            .summary-card { border: 1px solid #ddd; padding: 15px; border-radius: 5px; }
                            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
                            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px; }
                          </style>
                        </head>
                        <body>
                          ${printContent.innerHTML}
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => {
                      printWindow.print();
                      printWindow.close();
                    }, 250);
                  }
                }
                handlePrintClose();
              }}
            >
              Print Now
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading
          message="Loading ledger report..."
          spinner="circles"
          isOpen={status.loading}
        />
        <IonToast 
          position="middle" 
          isOpen={showToast} 
          message={errorLogin} 
          duration={3000}
          onDidDismiss={() => setShowToast(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default LedgerReport_sales_only;